/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */

/*
 * The live activity map on the exec and dashboard pages.  Points are pushed
 * from the server over the liveMap socket event as probes run, so unlike the
 * fingerprint map there is nothing to fold up front: the whole feed goes onto
 * one circle layer that the GPU redraws as it grows.
 */

import { useEffect, useContext, useState, useRef } from 'react';
import React from 'react';
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import { WsContext, WsContextType } from './WsContext';
import BaseMap from './BaseMap';
import { plottableAnnos } from '../utils/geoCoords';
import { STATES_10M } from '../utils/mapStyle';

const IP_COLOR = '#F0F';
const BUSINESS_COLOR = '#FF0';

const LiveMapInner = ({ geoFill, geoStroke, geoBg }:
                      { geoFill?: string, geoStroke?: string, geoBg?: string }) => {
    const wsContext = useContext(WsContext) as WsContextType;
    const [ data, setData ] = useState<any>(null);
    const [ map, setMap ] = useState<MapLibreMap | null>(null);

    /*
     * The handler has to be the same function object for on() and off(), or
     * the cleanup removes nothing and listeners pile up on every re-subscribe
     */
    const handler = useRef((msg: any) => {
        if (msg) {
            setData(msg);
        }
    });

    useEffect(() => {
        if (!wsContext.ws) {
            return;
        }
        const ws = wsContext.ws;
        const fn = handler.current;
        ws.on('liveMap', fn);
        return () => {
            ws.off('liveMap', fn);
        };
    }, [ wsContext ]);

    useEffect(() => {
        if (!map) {
            return;
        }
        map.addSource('live', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
        } as any);
        map.addLayer({
            id: 'live-points',
            type: 'circle',
            source: 'live',
            paint: {
                'circle-color': [ 'case', [ '==', [ 'get', 'type' ], 'ip' ],
                    IP_COLOR, BUSINESS_COLOR ] as any,
                'circle-radius': [ 'case', [ '==', [ 'get', 'type' ], 'ip' ], 3, 5 ] as any,
                'circle-opacity': 0.85,
                'circle-stroke-width': 1,
                'circle-stroke-color': 'rgba(0,0,0,0.5)'
            }
        });
    }, [ map ]);

    useEffect(() => {
        if (!map) {
            return;
        }
        const source = map.getSource('live') as GeoJSONSource | undefined;
        if (!source) {
            return;
        }
        const points = plottableAnnos<any>(data?.annos);
        source.setData({
            type: 'FeatureCollection',
            features: points.map((p: any) => ({
                type: 'Feature',
                properties: { type: p.type, title: p.title || '' },
                geometry: { type: 'Point', coordinates: [ p.lon, p.lat ] }
            }))
        } as any);
    }, [ map, data ]);

    return (
        <BaseMap
            /*
             * Admin-1 lines are on because a point landing in the US or China
             * says very little against the country outline alone.  That makes
             * the coarse 110m country set the wrong partner -- 10m state lines
             * would stand off a 110m coastline around the Great Lakes and the
             * Chesapeake once zoomed in -- so this takes BaseMap's 50m default
             * like every other map, and shares its cached fetch with them.
             *
             * City names stay off: this panel is a few hundred pixels tall, and
             * a label layer there would spend its collision budget arguing with
             * itself.
             */
            statesUrl={STATES_10M}
            citiesUrl={null}
            /*
             * state tracks the caller's stroke rather than the theme's own map
             * palette, since the pages hosting this widget pass a stroke of
             * their own (E2 uses textMuted) and a state line left on the theme
             * colour would not match the country lines beside it.  Width and
             * opacity in buildBaseStyle keep it subordinate, not colour.
             */
            colors={{ fill: geoFill, stroke: geoStroke, state: geoStroke, bg: geoBg } as any}
            onMapReady={setMap}
            center={[ 20, 10 ]}
            zoom={0.2}
            minZoom={0}
            maxZoom={10}
            graticule={false}
            navControl={false}
        />
    );
};

export const LiveMap = React.memo(LiveMapInner);
